import { useState } from 'react';
import { useRecoilState } from 'recoil';
import { Button, ButtonSize, ButtonType, ButtonVariant } from 'components/core';
import { builderAnswersState } from 'states';
import { canPickAnyChart, otherCharts } from 'helpers/kpiBuilder/chartChoices';
import { chartOptions, resolvedChart } from 'helpers/kpiBuilder/engine';
import { BuilderAnswers, ChartOption } from 'helpers/kpiBuilder/types';
import { BuilderSection } from './BuilderSection';
import { OptionCards } from './OptionCards';
import { TextBox } from './TextBox';

const HINT =
  'How the answer is drawn on the dashboard. The recommended chart is the best fit for your question. You can look at every chart type and pick another, but a chart that is not made for the question may not show it clearly.';

// Collapsed: the recommended chart, plus the chosen one if it is a different chart (even one not made for the question).
const visibleCharts = (a: BuilderAnswers, suited: ChartOption[], showAll: boolean): ChartOption[] => {
  if (!canPickAnyChart(a)) return suited;
  const every = [...suited, ...otherCharts(a, suited)];
  if (showAll) return every;
  const chosen = resolvedChart(a);
  return [suited[0], ...every.filter((c) => c.id === chosen && c.id !== suited[0].id)];
};

// Chart type: the recommended chart first, with every plain chart type available on request.
export const ChartTypeStep = () => {
  const [a, setA] = useRecoilState(builderAnswersState);
  const [showAll, setShowAll] = useState(false);
  const suited = chartOptions(a);

  return (
    <BuilderSection title="Chart type" hint={HINT}>
      <OptionCards
        cards={visibleCharts(a, suited, showAll).map((c) => ({ id: c.id, label: c.id === suited[0].id && a.recipe !== 'advanced' ? `${c.label} (Recommended)` : c.label, desc: c.desc }))}
        selected={[resolvedChart(a) ?? '']}
        onSelect={(id) => setA({ ...a, chart: id })}
      />
      {a.recipe === 'advanced' && (
        <div className="mt-3">
          <TextBox value={a.chart ?? ''} placeholder="Or type any chart type the dashboard knows" onChange={(chart) => setA({ ...a, chart: chart.trim() || null })} />
        </div>
      )}
      {canPickAnyChart(a) && (
        <div className="mt-3">
          <Button htmlType="button"
            size={ButtonSize.small}
            type={ButtonType.secondary}
            variant={ButtonVariant.outline}
            onClick={() => {
              setShowAll(!showAll);
            }}
          >
            {showAll ? 'Show recommended only' : 'See all chart types'}
          </Button>
        </div>
      )}
    </BuilderSection>
  );
};
